import React, { useEffect, useState, useCallback } from 'react';

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '-';
    const sizes = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
    if (bytes === 0) return '0 Б';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const val = bytes / Math.pow(1024, i);
    return `${val.toFixed(val >= 10 ? 0 : 1)} ${sizes[i]}`;
}

function minutesLeft(ts) {
    if (!ts) return '-';
    const now = Date.now();
    const diffMs = ts - now;
    if (diffMs <= 0) return 'истёк';
    const diffMin = Math.ceil(diffMs / 60000);
    return `${diffMin} мин`;
}

export default function ServerArchivesTab({ ws, deviceUuid }) {
    const [archives, setArchives] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState({}); // { fileName: {progress:0..100,status:'downloading'|'done'|'error'} }
    const token = localStorage.getItem('token');

    const requestArchives = useCallback(() => {
        if (!ws || ws.readyState !== 1) return;
        setLoading(true);
        ws.send(JSON.stringify({
            action: 'getServerSideArchives',
            uuid: deviceUuid
        }));
    }, [ws, deviceUuid]);

    useEffect(() => {
        requestArchives();
    }, [requestArchives]);

    useEffect(() => {
        if (!ws) return;

        function handleMessage(ev) {
            let msg;
            try {
                msg = JSON.parse(ev.data);
            } catch (e) {
                return;
            }

            if (msg.action === 'serverSideArchivesList') {
                const list = Array.isArray(msg.archives) ? msg.archives : [];
                setArchives(list);
                setLoading(false);
                return;
            }

            if (msg.action === 'serverSideDownloadProgress') {
                // опционально если сервер шлёт прогресс скачивания
                // {action:"serverSideDownloadProgress", fileName:"...", progress:42}
                if (!msg.fileName) return;
                setDownloading(prev => ({
                    ...prev,
                    [msg.fileName]: {
                        ...(prev[msg.fileName] || {}),
                        progress: typeof msg.progress === 'number' ? msg.progress : prev[msg.fileName]?.progress,
                        status: 'downloading'
                    }
                }));
                return;
            }

            if (msg.action === 'serverSideDownloadDone') {
                // опционально
                if (!msg.fileName) return;
                setDownloading(prev => ({
                    ...prev,
                    [msg.fileName]: {
                        ...(prev[msg.fileName] || {}),
                        progress: 100,
                        status: 'done'
                    }
                }));
                return;
            }
        }

        ws.addEventListener('message', handleMessage);
        return () => {
            ws.removeEventListener('message', handleMessage);
        };
    }, [ws]);

    function handleRefresh() {
        requestArchives();
    }

    function handleDownload(arc) {
        if (!arc || !arc.downloadUrl) return;

        // фиксируем "начали скачивание"
        setDownloading(prev => ({
            ...prev,
            [arc.fileName]: {
                progress: 0,
                status: 'downloading'
            }
        }));

        // обычная ссылка с токеном
        const url = `${arc.downloadUrl}?token=${encodeURIComponent(token || '')}`;

        // создаём скрытую ссылку и кликаем для скачивания ZIP
        const a = document.createElement('a');
        a.href = url;
        a.download = arc.fileName || 'archive.zip';
        document.body.appendChild(a);
        a.click();
        a.remove();

        // мы не знаем прогресс через браузерный download без XHR/стриминга,
        // так что просто помечаем как done через небольшой timeout.
        setTimeout(() => {
            setDownloading(prev => ({
                ...prev,
                [arc.fileName]: {
                    progress: 100,
                    status: 'done'
                }
            }));
        }, 2000);
    }

    return (
        <div className="info-section">
            <div className="info-header">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
                        Сохранённые на сервере
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                        Здесь хранятся временные ZIP (до 60 минут)
                    </div>
                </div>

                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                    <button
                        style={{
                            padding: '6px 10px',
                            fontSize: '12px',
                            backgroundColor: '#1f2937',
                            color: '#fff',
                            border: '1px solid #4b5563',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                        }}
                        onClick={handleRefresh}
                    >
                        Обновить список
                    </button>
                </div>
            </div>

            <div className="info-body" style={{ marginTop: '10px' }}>
                {loading ? (
                    <p style={{ fontSize: '13px', color: '#ccc' }}>Загрузка списка...</p>
                ) : archives.length === 0 ? (
                    <p style={{ fontSize: '13px', color: '#ccc' }}>
                        На сервере сейчас нет временных ZIP.
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table
                            style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '13px',
                                color: '#fff',
                                minWidth: '600px'
                            }}
                        >
                            <thead>
                                <tr style={{ textAlign: 'left', backgroundColor: '#1f2937' }}>
                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #374151', fontWeight: 500 }}>Имя файла</th>
                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #374151', fontWeight: 500, whiteSpace:'nowrap' }}>Размер</th>
                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #374151', fontWeight: 500, whiteSpace:'nowrap' }}>Осталось</th>
                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #374151', fontWeight: 500 }}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {archives.map((arc, idx) => {
                                    const info = downloading[arc.fileName];
                                    return (
                                        <tr
                                            key={arc.transferId || idx}
                                            style={{
                                                backgroundColor: idx % 2 === 0 ? '#111827' : '#1f2937'
                                            }}
                                        >
                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #374151', fontFamily: 'monospace', fontSize: '12px' }}>
                                                {arc.fileName || '-'}
                                            </td>
                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #374151', whiteSpace:'nowrap' }}>
                                                {formatBytes(arc.fileSize)}
                                            </td>
                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #374151', whiteSpace:'nowrap' }}>
                                                {minutesLeft(arc.expiresAt)}
                                            </td>
                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #374151' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                                                    <button
                                                        style={{
                                                            padding: '4px 8px',
                                                            fontSize: '12px',
                                                            backgroundColor: '#111827',
                                                            color: '#fff',
                                                            border: '1px solid #4b5563',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                        onClick={() => handleDownload(arc)}
                                                    >
                                                        Скачать ZIP
                                                    </button>

                                                    {info && (
                                                        <span style={{ fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                                                            {info.status === 'downloading' && `Загрузка... ${info.progress ?? 0}%`}
                                                            {info.status === 'done' && 'Готово'}
                                                            {info.status === 'error' && 'Ошибка'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
